import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = {
  en_attente_paiement: "En attente de paiement",
  confirmee: "Confirmée",
  prete: "Prête",
  terminee: "Terminée",
  annulee: "Annulée",
};

export default function OrdersManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("toutes");

  const load = () => api.get("/admin/orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status });
    toast.success("Statut mis à jour.");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/orders/${id}`);
    toast.success("Commande supprimée.");
    load();
  };

  const shown = items.filter((o) => filter === "toutes" || (o.source || "emporter") === filter);

  return (
    <div data-testid="orders-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ivory">Commandes — {shown.length}</h2>
        <div className="flex gap-2" data-testid="orders-filters">
          {[
            { id: "toutes", label: "Toutes" },
            { id: "emporter", label: "À emporter" },
            { id: "comptoir", label: "Comptoir" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`orders-filter-${f.id}`}
              className={`rounded-md px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                filter === f.id ? "bg-copper text-ivory" : "text-ivory/50 hover:text-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="orders-empty">
          Aucune commande pour le moment.
        </p>
      ) : (
    <div className="space-y-4" data-testid="orders-list">
      {shown.map((o) => (
        <div
          key={o.id}
          className="rounded-lg border border-white/10 bg-coal/60 p-5"
          data-testid={`order-${o.id}`}
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="min-w-40">
              <p className="text-sm font-semibold text-ivory">{o.name}</p>
              <p className="text-xs text-ivory/45">
                {o.phone} · {o.email}
              </p>
            </div>
            <div className="text-sm text-ivory/70">
              Retrait : {o.date} · {o.time}
            </div>
            <span className="rounded-md border border-gold/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">
              {(o.source || "emporter") === "comptoir" ? "Comptoir" : "À emporter"}
            </span>
            <div className="text-sm font-semibold text-gold">
              {String(o.total.toFixed(2)).replace(".", ",")} €
            </div>
            <div className="ml-auto flex items-center gap-3">
              <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                <SelectTrigger
                  className="!h-9 w-44 border-white/10 bg-white/5 text-xs text-ivory"
                  data-testid={`order-status-${o.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={() => remove(o.id)}
                aria-label="Supprimer"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400"
                data-testid={`order-delete-${o.id}`}
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <ul className="mt-3 space-y-1 border-t border-white/5 pt-3">
            {o.items.map((i, idx) => (
              <li key={idx} className="flex justify-between gap-4 text-xs text-ivory/55">
                <span>
                  {i.quantity} × {i.name}
                </span>
                <span>{i.price}</span>
              </li>
            ))}
          </ul>
          {o.notes && <p className="mt-2 text-xs italic text-ivory/40">{o.notes}</p>}
        </div>
      ))}
    </div>
      )}
    </div>
  );
}
